// src/features/medicine-inventory/context/InventoryContext.jsx
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

export const InventoryContext = createContext(null);

export const useInventory = () => {
  const context = useContext(InventoryContext);
  if (!context) {
    throw new Error('useInventory must be used within InventoryProvider');
  }
  return context;
};

export const InventoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load initial data
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Loading data from API...');
      const [categoriesRes, medicinesRes, movementsRes] = await Promise.all([
        api.getCategories(),
        api.getMedicines(),
        api.getStockMovements()
      ]);

      console.log('Data loaded:', {
        categories: categoriesRes.data,
        medicines: medicinesRes.data,
        movements: movementsRes.data
      });

      setCategories(categoriesRes.data || []);
      setMedicines(medicinesRes.data || []);
      setStockMovements(movementsRes.data || []);

      toast.success('Data loaded successfully');
    } catch (error) {
      console.error('Failed to load data:', error);
      setError(error.message);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Category CRUD
  const addCategory = async (categoryData) => {
    try {
      const response = await api.createCategory(categoryData);
      setCategories(prev => [...prev, response.data]);
      toast.success('Category added successfully');
      return response.data;
    } catch (error) {
      console.error('Failed to add category:', error);
      toast.error('Failed to add category');
      throw error;
    }
  };

  const updateCategory = async (id, categoryData) => {
    try {
      const response = await api.updateCategory(id, categoryData);
      setCategories(prev => prev.map(c => c.id === id ? response.data : c));
      toast.success('Category updated successfully');
      return response.data;
    } catch (error) {
      console.error('Failed to update category:', error);
      toast.error('Failed to update category');
      throw error;
    }
  };

  const deleteCategory = async (id) => {
    try {
      await api.deleteCategory(id);
      setCategories(prev => prev.filter(c => c.id !== id));
      toast.success('Category deleted successfully');
    } catch (error) {
      console.error('Failed to delete category:', error);
      toast.error('Failed to delete category');
      throw error;
    }
  };

  // Medicine CRUD
  const addMedicine = async (medicineData) => {
    try {
      const response = await api.createMedicine(medicineData);
      setMedicines(prev => [...prev, response.data]);
      toast.success('Medicine added successfully');
      return response.data;
    } catch (error) {
      console.error('Failed to add medicine:', error);
      toast.error('Failed to add medicine');
      throw error;
    }
  };

  const updateMedicine = async (id, medicineData) => {
    try {
      const response = await api.updateMedicine(id, medicineData);
      setMedicines(prev => prev.map(m => m.id === id ? response.data : m));
      toast.success('Medicine updated successfully');
      return response.data;
    } catch (error) {
      console.error('Failed to update medicine:', error);
      toast.error('Failed to update medicine');
      throw error;
    }
  };

  const deleteMedicine = async (id) => {
    try {
      await api.deleteMedicine(id);
      setMedicines(prev => prev.filter(m => m.id !== id));
      toast.success('Medicine deleted successfully');
    } catch (error) {
      console.error('Failed to delete medicine:', error);
      toast.error('Failed to delete medicine');
      throw error;
    }
  };

  // Stock Movements
  const addStockMovement = async (movementData) => {
    try {
      let response;

      if (movementData.type === 'MULTI_ISSUE') {
        response = await api.createMultiMedicineIssue(movementData);

        // Update all medicine stocks in state
        const updatedMedicines = [...medicines];
        movementData.medicines.forEach(item => {
          const medicineIndex = updatedMedicines.findIndex(m => m.id === item.medicineId);
          if (medicineIndex !== -1) {
            updatedMedicines[medicineIndex] = {
              ...updatedMedicines[medicineIndex],
              currentStock: updatedMedicines[medicineIndex].currentStock - item.quantity
            };
          }
        });
        setMedicines(updatedMedicines);
      } else {
        response = await api.createStockMovement(movementData);

        // Update single medicine stock
        const medicine = medicines.find(m => m.id === movementData.medicineId);
        if (medicine) {
          const updatedMedicine = {
            ...medicine,
            currentStock: movementData.type === 'PURCHASE'
              ? medicine.currentStock + parseInt(movementData.quantity)
              : medicine.currentStock - parseInt(movementData.quantity)
          };
          setMedicines(prev => prev.map(m => m.id === medicine.id ? updatedMedicine : m));
        }
      }

      setStockMovements(prev => [response.data, ...prev]);
      toast.success('Stock movement recorded successfully');
      return response.data;
    } catch (error) {
      console.error('Failed to record stock movement:', error);
      toast.error('Failed to record stock movement');
      throw error;
    }
  };

  const multiPurchaseStock = async (data) => {
    try {
      let response;
      response = await api.createMultiMedicinePurchase(data);
      return response.data;
    } catch (error) {
      console.error('Failed to record stock movement:', error);
      toast.error('Failed to record stock movement');
      throw error;
    }
  };

  const value = {
    categories,
    medicines,
    stockMovements,
    loading,
    error,
    addCategory,
    updateCategory,
    deleteCategory,
    addMedicine,
    updateMedicine,
    deleteMedicine,
    addStockMovement,
    multiPurchaseStock,
    refreshData: loadData,
  };

  return (
    <InventoryContext.Provider value={value}>
      {children}
    </InventoryContext.Provider>
  );
};

export default InventoryProvider;